// §7.4 — the sync protocol, client half (D-045).
//
// Four steps, in this order and for these reasons:
//
//   1. Pull. `GET /api/sync/changes?since=<cursor>` — what the server has that we do not.
//   2. Apply each remote change **where the local entry is not `dirty`**. A dirty entry is one we
//      have edited and not yet pushed; overwriting it with the server's copy would silently throw
//      away the user's work, which is the exact thing D-045 exists to make impossible.
//   3. Push each dirty entry, blob first (`HEAD` → 204 means the bytes are already there), then
//      the file with `If-Match: <rev>`. A `409` is not an error — it is the other half of the
//      conflict rule, and it produces a **conflict copy** rather than a loss.
//   4. Repeat while the server says there is more.
//
// The transport is injected rather than reached for. `fetch` in a test is a real HTTP call to a
// real server (§20 keeps everything offline, not everything faked), and a client that closes over
// the global cannot be pointed at one.
import {
  basename,
  joinVfs,
  parentOf,
  splitName,
  type SyncChange,
  type SyncChangesResponse,
  type SyncEntry,
  type VfsPath,
} from '@hangar/shared';

/** What the client remembers about each path between runs — §7.4 keeps this in OPFS. */
export interface SyncStateStore {
  get(path: VfsPath): SyncEntry | undefined;
  set(path: VfsPath, entry: SyncEntry): void;
  delete(path: VfsPath): void;
  paths(): VfsPath[];
  /** The last cursor the server handed out. `0` on a first run. */
  cursor(): number;
  setCursor(value: number): void;
}

/** The local bytes. In the browser this is OPFS (§7.2); in a test it is a map. */
export interface SyncFileStore {
  read(path: VfsPath): Promise<Uint8Array>;
  write(path: VfsPath, bytes: Uint8Array): Promise<void>;
  remove(path: VfsPath): Promise<void>;
  exists(path: VfsPath): Promise<boolean>;
  /**
   * D-194 — create a directory. Without this, `applyRemote` had nowhere to put an incoming
   * folder: it recorded the rev and returned, so the folder existed in the sync state and
   * nowhere a user could see it.
   */
  mkdir(path: VfsPath): Promise<void>;
  /**
   * §34.6 / D-496 — bytes on disk, **without reading them**.
   *
   * Added for the device-local threshold and for nothing else. The check has to happen before
   * `files.read`, because the whole point is a file too big to want in memory: a 4 GB recording that
   * was read and then skipped would be read again on every run, once a minute, forever.
   */
  size(path: VfsPath): Promise<number>;
}

export interface SyncTransport {
  /** `GET /api/sync/changes?since=` */
  changes(since: number): Promise<SyncChangesResponse>;
  /** `HEAD /api/blobs/:sha` — true when the server already holds these bytes. */
  hasBlob(sha: string): Promise<boolean>;
  /** `PUT /api/blobs/:sha` */
  putBlob(sha: string, bytes: Uint8Array): Promise<void>;
  /** `GET /api/blobs/:sha` */
  getBlob(sha: string): Promise<Uint8Array>;
  /**
   * `PUT /api/files?path=` with `If-Match`. Answers the new rev, or `'conflict'` — which is a
   * result, not a failure.
   */
  putFile(
    path: VfsPath,
    sha: string,
    size: number,
    ifMatch: number | undefined,
  ): Promise<{ ok: true; rev: number } | { ok: false; reason: 'conflict' }>;
  /**
   * D-194 — `POST /api/files/mkdir`. Idempotent: a folder that is already there answers its
   * existing rev rather than failing, because two devices creating the same folder is ordinary.
   * `'conflict'` means a **file** occupies that path, which no retry can fix.
   */
  putDir(path: VfsPath): Promise<{ ok: true; rev: number } | { ok: false; reason: 'conflict' }>;
  /**
   * `DELETE /api/files?path=` — §7.8's delete, which the server performs the same way the client
   * does: the file moves to *its* Trash and a `delete` change is appended at the original path.
   *
   * `'missing'` rather than a throw for a 404. A path deleted before it was ever pushed is the
   * ordinary case — create a file, change your mind, and the tombstone reaches a server that never
   * heard of it. There is nothing to undo and nothing to report.
   *
   * There is no conflict arm, and that is deliberate rather than an omission: the server's delete
   * is non-destructive (§7.8 — it trashes, and §7.5's versions survive), so a delete racing an
   * edit loses no bytes. It is recoverable from the Trash on either side, which is the property
   * D-045 exists to guarantee and the reason an `If-Match` here would buy nothing.
   */
  deleteFile(path: VfsPath): Promise<'deleted' | 'missing'>;
}

export interface SyncConflict {
  readonly path: VfsPath;
  readonly copy: VfsPath;
}

export interface SyncResult {
  readonly pulled: number;
  readonly pushed: number;
  readonly conflicts: readonly SyncConflict[];
  readonly cursor: number;
}

export interface SyncClientOptions {
  readonly transport: SyncTransport;
  readonly state: SyncStateStore;
  readonly files: SyncFileStore;
  readonly sha256: (bytes: Uint8Array) => Promise<string>;
  readonly now: () => number;
  /** §7.4 — "A notification is posted (§13)." The kernel supplies one; a test counts them. */
  readonly onConflict?: (conflict: SyncConflict) => void;
  /**
   * §34.6 / D-496 — is a file of this size device-local?
   *
   * Injected rather than read here, because the answer is a **§14 setting** and this module knows
   * nothing about settings. The kernel closes over `settings.get('media.syncThresholdMb')`, so the
   * predicate is re-evaluated on every run — which is what makes raising the threshold push a file
   * that was previously held back, with no extra bookkeeping.
   *
   * Absent, nothing is device-local, which is the pre-D-496 behaviour exactly.
   */
  readonly deviceLocal?: (sizeBytes: number) => boolean;
}

/**
 * D-045's conflict name: `` `<stem> (conflicted copy <ISO-8601>)<ext>` ``.
 *
 * Byte-identical to the server's `conflictCopyName` on purpose — a conflict copy the two ends spell
 * differently is two files, which is the failure the rule exists to prevent.
 */
export function conflictCopyName(path: VfsPath, at: number): VfsPath {
  const { stem, ext } = splitName(basename(path));
  return joinVfs(parentOf(path), `${stem} (conflicted copy ${new Date(at).toISOString()})${ext}`);
}

export interface SyncClient {
  sync(): Promise<SyncResult>;
  /** Mark a path edited locally, so the next run pushes it (§7.4's `dirty`). */
  markDirty(path: VfsPath, sha: string): void;
  /**
   * D-194 — mark a **directory** created locally, so the next run pushes it.
   *
   * Separate from `markDirty` for the reason `markDeleted` is: the three cannot share a push
   * path. A dirty entry is pushed by reading its bytes; a folder has none.
   */
  markDirectory(path: VfsPath): void;
  /**
   * §7.8 — mark a path deleted locally, so the next run tells the server.
   *
   * Kept separate from `markDirty` because the two cannot share a code path: a dirty entry is
   * pushed by reading its bytes, and a deleted one has none. Calling `markDirty` for a deletion
   * would make the next run throw on `files.read` and take every other pending change down with it.
   */
  markDeleted(path: VfsPath): void;
  cursor(): number;
}

/**
 * What one push attempt did.
 *
 * D-194 — `pushOne` used to answer only `SyncConflict | null`, and the caller inferred "pushed"
 * from the entry being clean afterwards. That counted every *already-clean* path as a push, so
 * `SyncResult.pushed` was really "how many paths this device holds" — a quiet run with fifty
 * synced files reported fifty pushes. Nothing depended on the number, which is why it went
 * unnoticed until a test asked whether a second run pushed anything.
 */
interface PushOutcome {
  readonly pushed: boolean;
  readonly conflict: SyncConflict | null;
}

const NOTHING_TO_PUSH: PushOutcome = { pushed: false, conflict: null };

export function createSyncClient(options: SyncClientOptions): SyncClient {
  const { transport, state, files, sha256, now } = options;

  async function applyRemote(change: SyncChange): Promise<boolean> {
    const local = state.get(change.path);
    // §7.4 step 2 — never over a dirty entry. The push in step 3 will find the conflict properly.
    if (local?.dirty === true) return false;

    if (change.op === 'delete') {
      if (await files.exists(change.path)) await files.remove(change.path);
      state.delete(change.path);
      return true;
    }
    if (change.isDir) {
      // D-194 — *create* it, then record the rev. Recording alone was the bug: the folder was
      // known to sync and invisible to the user, so an empty folder never crossed devices at
      // all. One with a file in it always appeared, because writing the file creates its
      // ancestors — which is exactly why this looked like "folders are slow" rather than
      // "folders do not sync".
      await files.mkdir(change.path);
      state.set(change.path, { rev: change.rev, sha: '', dirty: false, dir: true });
      return true;
    }
    if (change.sha === undefined) {
      // A `put` with no blob: nothing to fetch, so there is only the rev to record.
      state.set(change.path, { rev: change.rev, sha: '', dirty: false });
      return true;
    }
    // Already holding exactly these bytes — content addressing makes this free to check.
    if (local?.sha !== change.sha) {
      await files.write(change.path, await transport.getBlob(change.sha));
    }
    state.set(change.path, { rev: change.rev, sha: change.sha, dirty: false });
    return true;
  }

  async function pushOne(path: VfsPath): Promise<PushOutcome> {
    const entry = state.get(path);
    if (!entry?.dirty) return NOTHING_TO_PUSH;

    // §7.8 — a tombstone, before anything tries to read bytes that are not there.
    if (entry.deleted === true) {
      await transport.deleteFile(path);
      // Forget the path entirely rather than keeping a clean entry. The server's own `delete`
      // change will come back on a later pull, and `applyRemote` is a no-op for a file that is
      // already gone — whereas a lingering entry would be walked by every future run forever.
      state.delete(path);
      return { pushed: true, conflict: null };
    }

    // D-194 — a folder, before anything tries to hash bytes that do not exist. Same reasoning as
    // the tombstone above: `files.read` on a directory throws, and one throw ends the whole run.
    if (entry.dir === true) {
      const made = await transport.putDir(path);
      if (made.ok) {
        state.set(path, { rev: made.rev, sha: '', dirty: false, dir: true });
        return { pushed: true, conflict: null };
      }
      // A *file* holds that path on the server. There is no conflict copy to make — a folder has
      // no content to preserve — so the entry is cleared rather than retried forever, and the
      // next pull brings down whatever is actually there.
      state.delete(path);
      // Refused, not pushed: a file holds that path and no retry changes it.
      return NOTHING_TO_PUSH;
    }

    // §34.6 / D-496 — the device-local gate, **before the read**. The entry deliberately stays
    // dirty: a file held back is not a file that has been dealt with, and leaving the flag set is
    // what makes lowering *or* raising the threshold take effect on the very next run rather than
    // needing the file touched again. It costs one `size` call per run per over-threshold file.
    if (options.deviceLocal !== undefined && options.deviceLocal(await files.size(path))) {
      return NOTHING_TO_PUSH;
    }

    const bytes = await files.read(path);
    const sha = await sha256(bytes);

    // §7.4 step 3 — `HEAD` first. The server usually already has the bytes when a file was only
    // renamed, or when the same content exists elsewhere (D-047).
    if (!(await transport.hasBlob(sha))) {
      await transport.putBlob(sha, bytes);
    }

    const result = await transport.putFile(path, sha, bytes.length, entry.rev || undefined);
    if (result.ok) {
      state.set(path, { rev: result.rev, sha, dirty: false });
      return { pushed: true, conflict: null };
    }

    // 409 — D-045's conflict copy. **The server's content stays at `path`**; ours moves aside and
    // is synced as a new file, so nothing is overwritten in either direction.
    const copy = conflictCopyName(path, now());
    await files.write(copy, bytes);
    state.set(copy, { rev: 0, sha, dirty: true });

    // Re-pull `path` on the next round so the server's version lands where ours was; clearing the
    // dirty flag is what lets step 2 do it.
    state.set(path, { rev: entry.rev, sha: entry.sha, dirty: false });

    const conflict: SyncConflict = { path, copy };
    options.onConflict?.(conflict);
    return { pushed: false, conflict };
  }

  return {
    cursor: () => state.cursor(),

    markDirty(path, sha) {
      const existing = state.get(path);
      state.set(path, { rev: existing?.rev ?? 0, sha, dirty: true });
    },

    markDeleted(path) {
      const existing = state.get(path);
      state.set(path, { rev: existing?.rev ?? 0, sha: existing?.sha ?? '', dirty: true, deleted: true });
    },

    markDirectory(path) {
      const existing = state.get(path);
      // Already known and not dirty means it came *from* the server on a pull, and re-pushing it
      // would be this device announcing a folder it was just told about.
      if (existing?.dir === true && !existing.dirty) return;
      state.set(path, { rev: existing?.rev ?? 0, sha: '', dirty: true, dir: true });
    },

    async sync(): Promise<SyncResult> {
      let pulled = 0;
      let pushed = 0;
      const conflicts: SyncConflict[] = [];

      // Step 1–2, following pagination to the end.
      for (;;) {
        const page = await transport.changes(state.cursor());
        for (const change of page.changes) {
          if (await applyRemote(change)) pulled += 1;
        }
        state.setCursor(page.cursor);
        if (!page.more) break;
      }

      // Step 3, "in path order" — so a directory's contents go up in a stable, reproducible
      // sequence rather than whatever order a map happened to hold them in.
      for (const path of [...state.paths()].sort()) {
        const outcome = await pushOne(path);
        if (outcome.conflict) conflicts.push(outcome.conflict);
        if (outcome.pushed) pushed += 1;
      }

      // A conflict copy is itself a new local file; push it in the same run rather than leaving
      // the user's rescued work sitting unsynced until the next one.
      for (const conflict of conflicts) {
        await pushOne(conflict.copy);
      }

      // …and pull once more, so the server's version of a conflicted path lands locally now that
      // step 3 cleared the dirty flag.
      if (conflicts.length > 0) {
        const page = await transport.changes(0);
        for (const change of page.changes) {
          if (conflicts.some((conflict) => conflict.path === change.path)) {
            state.delete(change.path);
            if (await applyRemote(change)) pulled += 1;
          }
        }
      }

      return { pulled, pushed, conflicts, cursor: state.cursor() };
    },
  };
}

/** An in-memory `SyncStateStore`. The kernel persists one to OPFS; this is the shape it keeps. */
export function createMemorySyncState(): SyncStateStore {
  const entries = new Map<VfsPath, SyncEntry>();
  let cursor = 0;
  return {
    get: (path) => entries.get(path),
    set: (path, entry) => void entries.set(path, entry),
    delete: (path) => void entries.delete(path),
    paths: () => [...entries.keys()],
    cursor: () => cursor,
    setCursor: (value) => {
      // Monotonic on this side too: a page that answered with an older cursor must never rewind
      // one we already passed, or the next run replays changes we have applied.
      cursor = Math.max(cursor, value);
    },
  };
}
